import { ZomentumStorageKeys, cache } from "@/Contracts";
import { AuthToken } from "@/Contracts/Login";
import { AxiosRequestConfig } from "axios";

const baseAppUrl: string | undefined = process.env.REACT_APP_API_HOST;
interface AxiosCurlirizeRequestConfig extends AxiosRequestConfig {
	curlirize: boolean;
}

const setHeaders = (
	urlEncodedForm: boolean,
	multipartFormUpload: boolean,
	useToken: boolean,
	userIp?: string,
	customHeaders?: Record<string, string>
) => {
	const authToken = cache.get(ZomentumStorageKeys.Token, true) as AuthToken;

	const userIpAddress =
		(userIp?.trim()?.length && userIp) ??
		(cache.get(ZomentumStorageKeys.UserIp, true) as string);
	const updatedCustomHeaders = customHeaders
		? Object.entries(customHeaders).reduce(
				(final, [key, value]: [string, string]) => {
					if (value) {
						final = {
							...final,
							[key]: value
						};
					}
					return final;
				},
				{}
		  )
		: {};

	const additionalHeaders: Record<string, string> = {
		...updatedCustomHeaders
	};

	if (authToken && useToken) {
		additionalHeaders["Authorization"] = "Bearer " + authToken.token;
	}
	if (urlEncodedForm) {
		additionalHeaders["Content-Type"] =
			"application/x-www-form-urlencoded; charset=utf-8";
	} else if (multipartFormUpload) {
		additionalHeaders["Content-Type"] = "multipart/form-data";
	} else {
		additionalHeaders["Content-Type"] = "application/json";
		additionalHeaders["Accept"] = "application/json";
	}
	if (userIpAddress) additionalHeaders["X-Forwarded-For"] = userIpAddress;
	additionalHeaders["Cache-Control"] = "no-cache";
	additionalHeaders["Pragma"] = "no-cache";

	return additionalHeaders;
};

export const getRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded: boolean,
	isFileDownload: boolean,
	userIp?: string,
	customHeaders?: Record<string, string>
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "GET",
		baseURL: baseAppUrl,
		url: url,
		curlirize: true
	};
	if (data) {
		options.data = data;
	}
	if (isFileDownload) {
		options.responseType = "blob";
	}
	options.headers = setHeaders(
		urlEncoded,
		false,
		useToken,
		userIp,
		customHeaders
	);
	return options;
};

export const postRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded?: boolean,
	multipartFormUpload?: boolean,
	isFileDownload?: boolean,
	customHeaders?: Record<string, string>
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "POST",
		baseURL: baseAppUrl,
		url: url,
		curlirize: false
	};
	if (data) {
		options.data = data;
	}
	if (isFileDownload) {
		options.responseType = "blob";
	}
	options.headers = setHeaders(
		urlEncoded || false,
		multipartFormUpload || false,
		useToken,
		undefined,
		customHeaders
	);
	return options;
};

export const putRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded: boolean,
	multipartFormUpload: boolean,
	customHeaders?: Record<string, string>
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "PUT",
		baseURL: baseAppUrl,
		url: url,
		curlirize: false
	};

	if (data) {
		options.data = data;
	}

	options.headers = setHeaders(
		urlEncoded,
		multipartFormUpload,
		useToken,
		undefined,
		customHeaders
	);

	return options;
};

export const deleteRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded: boolean
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "DELETE",
		baseURL: baseAppUrl,
		url: url,
		curlirize: false
	};
	if (data) {
		options.data = data;
	}
	options.headers = setHeaders(urlEncoded, false, useToken);
	return options;
};

export const patchRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded: boolean,
	multipartFormUpload: boolean
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "PATCH",
		baseURL: baseAppUrl,
		url: url,
		curlirize: false
	};
	if (data) {
		options.data = data;
	}
	options.headers = setHeaders(urlEncoded, multipartFormUpload, useToken);
	return options;
};

export const genericGetRespect = (
	url: string,
	params: { [key: string]: any } = {},
	headers: { [key: string]: string } = {}
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "GET",
		url,
		headers,
		params,
		curlirize: false
	};

	return options;
};

export const genericPostConfig = (
	url: string,
	data: { [key: string]: any } = {},
	headers: { [key: string]: string } = {}
): AxiosRequestConfig => {
	const options: AxiosCurlirizeRequestConfig = {
		method: "POST",
		url,
		data,
		headers,
		curlirize: false
	};

	return options;
};

export const postFetchRequest = (
	url: string,
	data: any,
	useToken: boolean,
	urlEncoded?: boolean,
	multipartFormUpload?: boolean
): any => {
	const options = {
		keepalive: true,
		method: "PUT",
		headers: setHeaders(
			urlEncoded || false,
			multipartFormUpload || false,
			useToken
		),
		body: JSON.stringify(data)
	};
	return { options, apiUrl: baseAppUrl + url };
};
